import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { comparisonPlans, comparisonFeatures } from "@/mocks/comparisonPlans";
import { Star, Check, X, Filter, ChevronDown } from "lucide-react";

export default function ComparePlansPage() {
  const [selectedPlans, setSelectedPlans] = useState<string[]>([
    comparisonPlans[0].id,
    comparisonPlans[1].id,
  ]);
  const [showFilters, setShowFilters] = useState(false);
  const [ageFilter, setAgeFilter] = useState("31-40");
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/compare-plans`;

  const schemas = [
    webpageSchema(
      "Compare Insurance Plans Side-by-Side | ExpertAdvise.in",
      "Unbiased insurance plan comparison engine. Compare health, term, and life insurance plans from 20+ insurers on premium, coverage, claim ratio, and benefits.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Compare Plans", url: pageUrl },
    ]),
  ];

  const plansToCompare = useMemo(
    () => comparisonPlans.filter((p) => selectedPlans.includes(p.id)),
    [selectedPlans]
  );

  const togglePlan = (id: string) => {
    setSelectedPlans((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 2) return prev;
        return prev.filter((p) => p !== id);
      }
      if (prev.length >= 4) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const ageMultiplier = useMemo(() => {
    const map: Record<string, number> = { "18-30": 0.7, "31-40": 1, "41-50": 1.4, "51-60": 2.1, "60+": 3.5 };
    return map[ageFilter] || 1;
  }, [ageFilter]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Compare Insurance Plans Side-by-Side | ExpertAdvise.in"
        description="Unbiased insurance plan comparison engine. Compare health, term, and life insurance plans from 20+ insurers on premium, coverage, claim ratio, and benefits."
        keywords="compare insurance plans, health insurance comparison, term insurance comparison, best insurance plan India, compare life insurance"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Compare Plans" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Plan Comparison Engine
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Compare Insurance Plans Side-by-Side
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Unbiased comparison of top health insurance plans from India&apos;s leading insurers. 
              Select up to 4 plans and compare features, premiums, coverage, and benefits.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-slate-100 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors cursor-pointer"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
            <select
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              className="px-4 py-2 bg-slate-100 rounded-lg text-sm font-medium text-slate-700 border-none outline-none cursor-pointer"
            >
              <option value="18-30">Age: 18-30</option>
              <option value="31-40">Age: 31-40</option>
              <option value="41-50">Age: 41-50</option>
              <option value="51-60">Age: 51-60</option>
              <option value="60+">Age: 60+</option>
            </select>
            <div className="ml-auto text-sm text-slate-500">
              Comparing <span className="font-semibold text-slate-900">{plansToCompare.length}</span> of 4 max
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {comparisonPlans.map((plan) => {
                const isSelected = selectedPlans.includes(plan.id);
                return (
                  <button
                    key={plan.id}
                    onClick={() => togglePlan(plan.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center border ${
                        isSelected ? "bg-primary-500 border-primary-500" : "border-slate-300"
                      }`}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{plan.insurer}</div>
                      <div className="text-xs text-slate-500">{plan.planName}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {/* Header Row */}
            <div className="grid grid-cols-[180px_1fr] md:grid-cols-[220px_1fr] border-b border-slate-200">
              <div className="p-4 md:p-5 bg-slate-50 border-r border-slate-200 flex items-end">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Features</span>
              </div>
              <div
                className="grid overflow-x-auto"
                style={{ gridTemplateColumns: `repeat(${plansToCompare.length}, minmax(200px, 1fr))` }}
              >
                {plansToCompare.map((plan) => (
                  <div key={plan.id} className="p-4 md:p-5 border-r border-slate-100 last:border-r-0 text-center min-w-[200px]">
                    <div className="text-sm font-bold text-slate-900 mb-1">{plan.insurer}</div>
                    <div className="text-xs text-slate-500 mb-3">{plan.planName}</div>
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${
                            i < Math.floor(plan.rating) ? "text-amber-400 fill-amber-400" : "text-slate-300"
                          }`}
                        />
                      ))}
                      <span className="text-xs text-slate-600 ml-1">{plan.rating}</span>
                    </div>
                    <Link
                      to="/book-appointment?source=Compare%20Plans%20Page"
                      className="inline-block bg-cta-600 hover:bg-cta-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                    >
                      Get Quote
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Feature Rows */}
            {comparisonFeatures.map((feature) => (
              <div
                key={feature.key}
                className="grid grid-cols-[180px_1fr] md:grid-cols-[220px_1fr] border-b border-slate-100 last:border-b-0"
              >
                <div className="p-4 md:p-5 bg-slate-50 border-r border-slate-100 text-sm font-medium text-slate-700 flex items-center">
                  {feature.label}
                </div>
                <div
                  className="grid overflow-x-auto"
                  style={{ gridTemplateColumns: `repeat(${plansToCompare.length}, minmax(200px, 1fr))` }}
                >
                  {plansToCompare.map((plan) => {
                    const value = plan[feature.key as keyof typeof plan];
                    const isAddOns = feature.key === "addOns";
                    const isCashless = feature.key === "cashlessHospitals";
                    const displayValue = isAddOns
                      ? (value as string[]).join(", ")
                      : isCashless
                      ? `${value.toLocaleString()}+`
                      : String(value);

                    const isPositive =
                      typeof displayValue === "string" &&
                      (displayValue.includes("Covered") ||
                        displayValue.includes("No Sublimit") ||
                        displayValue.includes("All"));
                    const isNegative =
                      typeof displayValue === "string" && displayValue.includes("Not");

                    return (
                      <div
                        key={plan.id}
                        className="p-4 md:p-5 border-r border-slate-50 last:border-r-0 text-sm text-slate-700 flex items-center gap-2 min-w-[200px]"
                      >
                        {isPositive && <Check className="w-4 h-4 text-green-500 shrink-0" />}
                        {isNegative && <X className="w-4 h-4 text-red-400 shrink-0" />}
                        <span className={isPositive ? "text-green-700 font-medium" : isNegative ? "text-red-600" : ""}>
                          {feature.key === "premiumMonthly"
                            ? `₹${Math.round((value as number) * ageMultiplier).toLocaleString()}/mo`
                            : displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-slate-500 mt-4 text-center">
            * Premiums are indicative and based on a 35-year-old individual. Actual premiums may vary based on age, health history, and insurer underwriting.
            All data sourced from insurer public disclosures and IRDAI annual reports.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Still Confused Which Plan to Choose?
          </h2>
          <p className="text-slate-600 mb-6">
            Our advisors will analyze your needs and recommend the best plan. Zero cost, zero obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/book-appointment?source=Compare%20Plans%20Page"
              className="bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
            >
              Talk to an Expert
            </Link>
            <a
              href="https://wa.me/919999365655"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 rounded-full border border-slate-200 transition-colors whitespace-nowrap"
            >
              WhatsApp Expert
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}